import { useEffect, useMemo } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import usePlanStore, { DdxFixedTier } from 'store/planStore';
import { Countries } from 'shared/enums';
import HelperTooltip from '../../../../../../shared/HelperTooltip/HelperTooltip';
import linksConfig from '../../../../../../config/linking';
import { formatNumberWithSpaces } from '../../../common/BudgetTable/utils/formatNumberWithSpaces';
import DDXSummary from '../DDXSummary/DDXSummary';

type Selection = 'omitted' | 'percentage' | 'fixed';
type OneFixedTier<T> = Record<`fixedTier${number}`, T>;

type Props = {
  countryName: string;
  baseBudget: number;
  currency: string;
  format: (v: string | number) => string;
  isDDXLoading: boolean;
};

function percentLabel(v: number) {
  const p = v * 100;
  return `${Number.isInteger(p) ? p : p.toFixed(1)}%`;
}

function computeTotalDDX(
  type: 'fixed' | 'percentage' | 'omitted',
  value: number | undefined | null,
  baseBudget: number
): number | undefined | null {
  if (type === 'omitted') return null;
  if (type === 'fixed') return typeof value === 'number' ? value : undefined;
  if (type === 'percentage')
    return typeof value === 'number' ? baseBudget * value : undefined;
  return undefined;
}

function extractFixedTiers(tiers?: OneFixedTier<DdxFixedTier>[]): number[] {
  if (!tiers) return [];
  const result: number[] = [];
  tiers.forEach((tier) => {
    const v = Object.values(tier)[0];
    if (typeof v === 'number' && v > 0) result.push(v);
  });
  return result;
}

function countryNameToISO(countryName: string): string | null {
  return (
    (Countries as unknown as Record<string, string>)[
      countryName.toUpperCase()
    ] ?? null
  );
}

function isDdxPicked(
  ddx?: {
    type?: 'omitted' | 'percentage' | 'fixed';
    value?: number | null;
  } | null
) {
  if (!ddx?.type) return false;
  if (ddx.type === 'omitted') return true;
  return typeof ddx.value === 'number';
}

function isSelection(v: any): v is Selection {
  return v === 'omitted' || v === 'percentage' || v === 'fixed';
}

function isDdxNotApplicableSetup(setup: any, fixedTiers: number[]) {
  if (!setup) return true;

  const noMustHave = setup.isMustHave == null;
  const noPercentage = setup.isPercentage == null;
  const noFixedFlag = setup.isFixed == null;

  const noFixedTiers = fixedTiers.length === 0;

  return noMustHave && noPercentage && noFixedFlag && noFixedTiers;
}

export default function DDXRadios({
  countryName,
  baseBudget,
  format,
  currency,
  isDDXLoading,
}: Props) {
  const ddxSetupByCountry = usePlanStore((s) => s.ddxSetupByCountry);
  const ddx = usePlanStore(
    (s) => s.storedCountryForms?.[countryName]?.ddx ?? null
  );

  const setCountryDdx = usePlanStore((s) => s.setCountryDdx);
  const storeCountryFormState = usePlanStore((s) => s.storeCountryFormState);

  const countryISO = useMemo(
    () => countryNameToISO(countryName),
    [countryName]
  );

  const setup = useMemo(() => {
    if (!countryISO) return null;
    return ddxSetupByCountry?.[countryISO] ?? null;
  }, [countryISO, ddxSetupByCountry]);

  const percentage = setup?.isPercentage ?? null;

  const fixedTiers = useMemo(
    () => extractFixedTiers(setup?.tiers as any),
    [setup?.tiers]
  );

  const mustHave = setup?.isMustHave === 1;

  const showOmitted = !!setup && !mustHave;
  const showPercentage = !!setup && typeof percentage === 'number';
  const showFixed = !!setup && fixedTiers.length > 0;

  const options = useMemo(() => {
    const ops: Selection[] = [];
    if (showOmitted) ops.push('omitted');
    if (showPercentage) ops.push('percentage');
    if (showFixed) ops.push('fixed');
    return ops;
  }, [showOmitted, showPercentage, showFixed]);

  const ddxIsNulls =
    (ddx as any)?.type == null &&
    (ddx as any)?.value == null &&
    (ddx as any)?.totalDDX == null;

  const writeDdx = (next: { type: Selection; value?: number }) => {
    const totalDDX = computeTotalDDX(next.type, next.value, baseBudget);

    setCountryDdx(countryName, {
      type: next.type,
      value: next.type === 'omitted' ? null : (next.value as number),
      totalDDX: next.type === 'omitted' ? null : totalDDX,
    } as any);

    storeCountryFormState(countryName);
  };

  const writeDdxNulls = () => {
    setCountryDdx(countryName, {
      type: null,
      value: null,
      totalDDX: null,
    } as any);
    storeCountryFormState(countryName);
  };

  const ddxNotApplicable = isDdxNotApplicableSetup(setup, fixedTiers);

  useEffect(() => {
    if (!countryISO) return;

    if (ddxNotApplicable) {
      if (!ddxIsNulls) writeDdxNulls();
      return;
    }

    if (ddxIsNulls) {
      setCountryDdx(countryName, null);
      storeCountryFormState(countryName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryISO, ddxNotApplicable]);

  useEffect(() => {
    if (!setup) return;
    if (!ddx) return;
    if (ddxNotApplicable) return;
    if (ddxIsNulls) return;

    if ((ddx as any).type === 'omitted') return;

    if ((ddx as any).type === 'percentage') {
      const ok = typeof percentage === 'number';
      if (!ok) {
        setCountryDdx(countryName, null);
        storeCountryFormState(countryName);
      }
      return;
    }

    if ((ddx as any).type === 'fixed') {
      const ok =
        typeof (ddx as any).value === 'number' &&
        fixedTiers.includes((ddx as any).value);
      if (!ok) {
        setCountryDdx(countryName, null);
        storeCountryFormState(countryName);
      }
    }
  }, [
    setup,
    (ddx as any)?.type,
    (ddx as any)?.value,
    percentage,
    fixedTiers,
    ddxNotApplicable,
    ddxIsNulls,
  ]);

  useEffect(() => {
    if (!setup) return;
    if (ddxNotApplicable) return;
    if (ddx && !ddxIsNulls) return;
    if (options.length !== 1) return;

    const only = options[0];

    if (only === 'omitted') {
      writeDdx({ type: 'omitted' });
      return;
    }

    if (only === 'percentage') {
      if (typeof percentage !== 'number') return;
      writeDdx({ type: 'percentage', value: percentage });
      return;
    }

    if (only === 'fixed') {
      if (fixedTiers.length !== 1) return;
      writeDdx({ type: 'fixed', value: fixedTiers[0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setup,
    ddx,
    ddxIsNulls,
    options,
    percentage,
    fixedTiers,
    ddxNotApplicable,
  ]);

  const selection: '' | Selection =
    ddx && isSelection((ddx as any).type)
      ? ((ddx as any).type as Selection)
      : '';

  const fixedValue: number | '' =
    (ddx as any)?.type === 'fixed' && typeof (ddx as any).value === 'number'
      ? (ddx as any).value
      : '';

  if (!countryISO) return null;
  if (ddxNotApplicable) return null;
  if (!setup) return null;
  if (isDDXLoading) return <CircularProgress />;

  const borderOk = isDdxPicked(ddx);

  return (
    <Box sx={{ px: '15px', pt: '24px', background: '#eeeeee' }}>
      <Typography fontWeight={700} mb={3}>
        DDX 1P ACQUISITION
        <HelperTooltip
          tooltipData={linksConfig.ddx1pAcquistion}
          sx={{
            marginLeft: '24px',
            paddingBottom: 0,
            top: '-7px',
            position: 'relative',
            width: 'fit-content',
          }}
        />
      </Typography>

      <Box
        sx={{
          border: '1px solid',
          borderColor: borderOk ? '#c7d8f9' : '#F00F00',
          borderRadius: '5px',
        }}
      >
        <FormControl sx={{ width: '100%' }}>
          <RadioGroup
            value={selection}
            sx={{
              gap: '48px',
              py: '46px',
              background: '#ffffff',
              pl: '44px',
            }}
            onChange={(e) => {
              const v = e.target.value as Selection;

              if (v === 'omitted') {
                writeDdx({ type: 'omitted' });
                return;
              }

              if (v === 'percentage') {
                if (typeof percentage !== 'number') return;
                writeDdx({ type: 'percentage', value: percentage });
                return;
              }

              if (v === 'fixed') {
                const first = fixedTiers[0];
                if (typeof first !== 'number') return;
                writeDdx({ type: 'fixed', value: first });
              }
            }}
          >
            {showOmitted && (
              <FormControlLabel
                value="omitted"
                control={<Radio />}
                label={
                  <Typography fontSize="14px" fontWeight={700}>
                    No 1P Data Acquisition
                  </Typography>
                }
              />
            )}

            {showPercentage && typeof percentage === 'number' && (
              <FormControlLabel
                value="percentage"
                control={<Radio />}
                label={
                  <Typography fontSize="14px" fontWeight={700}>
                    Division Rate ({percentLabel(percentage)})
                  </Typography>
                }
              />
            )}

            {showFixed && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControlLabel
                  value="fixed"
                  control={<Radio />}
                  label={
                    <Typography fontSize="14px" fontWeight={700}>
                      Division Rate
                    </Typography>
                  }
                />

                <FormControl
                  size="small"
                  variant="outlined"
                  sx={{
                    minWidth: 264,
                    '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                      top: '50%',
                      transform: 'translate(14px, -50%)',
                    },
                  }}
                >
                  <InputLabel id={`ddx-rate-label-${countryISO}`}>
                    DDX rate
                  </InputLabel>

                  <Select
                    labelId={`ddx-rate-label-${countryISO}`}
                    id={`ddx-rate-${countryISO}`}
                    value={fixedValue}
                    label="DDX rate"
                    disabled={selection !== 'fixed'}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      if (!Number.isFinite(val)) return;
                      writeDdx({ type: 'fixed', value: val });
                    }}
                  >
                    {fixedTiers.map((v) => (
                      <MenuItem key={v} value={v}>
                        {formatNumberWithSpaces(v)} PLN
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </RadioGroup>
        </FormControl>

        <DDXSummary
          countryName={countryName}
          baseBudget={baseBudget}
          currency={currency}
          format={format}
        />
      </Box>
    </Box>
  );
}
